import { ChangeDetectionStrategy, Component, ElementRef, afterRenderEffect, computed, inject, signal, viewChild } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { NavigationStart, Router } from '@angular/router';
import { ButtonModule } from 'primeng/button';
import { PrimeNG } from 'primeng/config';
import { ZIndexUtils } from 'primeng/utils';
import { filter } from 'rxjs/operators';
import { SignInAsRefusalService } from '@core/services/sign-in-as-refusal.service';

/** The callout's width; it sits under the refused control and never runs off screen. */
const WIDTH_PX = 360;
const GAP_PX = 8;
const EDGE_PX = 16;
/** With no control to point at, the callout sits under the top bar. */
const FALLBACK_TOP_PX = 72;
/** Stacked in PrimeNG's modal layer, so it lands above any open dialog and its mask. */
const Z_INDEX_LAYER = 'modal';

/**
 * Says why an action was refused while an admin is signed in as someone, right under what was clicked, or above it when
 * there is no room below (US 3691). Closes on its button, on Escape, on a click anywhere else and on navigation.
 */
@Component({
  selector: 'app-sign-in-as-refusal',
  standalone: true,
  imports: [ButtonModule],
  templateUrl: './sign-in-as-refusal.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: {
    '(document:keydown.escape)': 'dismiss()',
    '(document:click)': 'onDocumentClick($event)'
  }
})
export class SignInAsRefusalComponent {
  private readonly refusalService = inject(SignInAsRefusalService);
  private readonly host = inject<ElementRef<HTMLElement>>(ElementRef);
  private readonly primeng = inject(PrimeNG);
  private readonly callout = viewChild<ElementRef<HTMLElement>>('callout');
  /** The rendered callout's height, measured after each render; it decides whether the callout fits below the control. */
  private readonly height = signal(0);

  protected readonly refusal = this.refusalService.refusal;
  protected readonly width = WIDTH_PX;
  protected readonly position = computed(() => {
    const anchor = this.refusal()?.anchor;
    if (!anchor) {
      return { top: FALLBACK_TOP_PX, left: Math.max(EDGE_PX, (window.innerWidth - WIDTH_PX) / 2) };
    }
    const height = this.height();
    const maxLeft = Math.max(EDGE_PX, window.innerWidth - WIDTH_PX - EDGE_PX);
    const below = anchor.bottom + GAP_PX;
    const top = below + height > window.innerHeight ? anchor.top - GAP_PX - height : below;
    const maxTop = Math.max(EDGE_PX, window.innerHeight - height - EDGE_PX);
    return { top: Math.min(Math.max(EDGE_PX, top), maxTop), left: Math.min(Math.max(EDGE_PX, anchor.left), maxLeft) };
  });

  constructor() {
    afterRenderEffect((onCleanup) => {
      this.refusal();
      const element = this.callout()?.nativeElement;
      if (!element) {
        this.height.set(0);
        return;
      }
      ZIndexUtils.set(Z_INDEX_LAYER, element, this.primeng.zIndex.modal);
      onCleanup(() => ZIndexUtils.clear(element));
      this.height.set(element.offsetHeight);
    });

    // The refused control is gone once the page changes.
    inject(Router)
      .events.pipe(
        filter((event) => event instanceof NavigationStart),
        takeUntilDestroyed()
      )
      .subscribe(() => this.dismiss());
  }

  protected dismiss(): void {
    this.refusalService.dismiss();
  }

  protected onDocumentClick(event: MouseEvent): void {
    // The refused click lands before the answer arrives; any later click outside the callout closes it.
    if (this.refusal() && !this.host.nativeElement.contains(event.target as Node)) {
      this.dismiss();
    }
  }
}
